import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { Icon } from '../icons/icons';
import styles from './SelectField.module.scss';
export type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'style' | 'size' | 'multiple'> & {
  /**
   * The label of an empty first option (`value=""`), rendered before the
   * options. The caller decides when to pass it (Field.js passes it while
   * the value is empty).
   */
  placeholderOption?: string;
  className?: never;
  style?: never;
};

/**
 * A native `<select>` on the Helios field look, with a chevron in place of
 * the native arrow. Every prop lands on the `<select>`, which takes the
 * forwarded ref; the wrapper `<span data-helios-control="select">` only
 * positions the chevron. The error state is `aria-invalid` (FormGroup sets
 * it).
 *
 * The chevron is Helios's internal `chevron-down` icon (Font Awesome Pro 6.7.2
 * regular/chevron-down.svg, U+F078; `icons/icons.tsx`): one em, aria-hidden,
 * currentColor.
 */
const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(t0, ref) {
  const $ = _c(14);
  let children;
  let placeholderOption;
  let props;
  if ($[0] !== t0) {
    const {
      placeholderOption: t1,
      children: t2,
      className: _className,
      style: _style,
      ...t3
    } = t0;
    placeholderOption = t1;
    children = t2;
    props = t3;
    $[0] = t0;
    $[1] = children;
    $[2] = placeholderOption;
    $[3] = props;
  } else {
    children = $[1];
    placeholderOption = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== placeholderOption) {
    t1 = placeholderOption !== undefined && <option value="">{placeholderOption}</option>;
    $[4] = placeholderOption;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <select {...props} ref={ref} className={styles["helios-select-field__select"]}>{t1}{children}</select>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon className={classNames(styles["helios-select-field__chevron"])} name="chevron-down" />;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2) {
    t4 = <span data-helios-control="select" className={styles["helios-select-field"]}>{t2}{t3}</span>;
    $[12] = t2;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
});
export default SelectField;